import React from "react";
import styled from "styled-components";
import { theme } from "src/styles/Theme";
import { WithdrawalRequest } from "src/types/withdrawalRequest";

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
`;

const ModalContent = styled.div`
	background: white;
	border-radius: 16px;
	padding: 24px;
	width: 90%;
	max-width: 400px;
	position: relative;
	box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const CloseButton = styled.button`
	position: absolute;
	top: 16px;
	right: 16px;
	background: none;
	border: none;
	font-size: 20px;
	cursor: pointer;
	color: #666;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;

	&:hover {
		background: #f5f5f5;
	}
`;

const Title = styled.h2`
	text-align: center;
	font-size: 20px;
	font-weight: 600;
	margin-bottom: 20px;
	color: ${(props) => props.theme.colors.text_01};
`;

const UserSection = styled.div`
	text-align: center;
	margin-bottom: 24px;
`;

const Avatar = styled.img`
	width: 80px;
	height: 80px;
	border-radius: 50%;
	margin-bottom: 12px;
	object-fit: cover;
`;

const UserName = styled.h3`
	font-size: 18px;
	font-weight: 600;
	margin-bottom: 4px;
	color: ${(props) => props.theme.colors.text_01};
`;

const UserType = styled.span`
	font-size: 14px;
	color: #ff6b35;
	font-weight: 500;
`;

const DetailsSection = styled.div`
	margin-bottom: 24px;
`;

const DetailRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 0;
	border-bottom: 1px solid #f0f0f0;

	&:last-child {
		border-bottom: none;
	}
`;

const DetailLabel = styled.span`
	font-size: 14px;
	color: #666;
	font-weight: 500;
`;

const DetailValue = styled.span<{ status?: string }>`
	font-size: 14px;
	font-weight: 600;
	color: ${(props) => {
		if (props.status === "pending") return "#55C4F1";
		if (props.status === "approved") return theme.colors.purple;
		if (props.status === "rejected") return "#EB5656";
		return props.theme.colors.text_01;
	}};
	background: ${(props) => {
		if (props.status === "pending") return "#E8F0FE";
		if (props.status === "approved") return "#F3E8FF";
		if (props.status === "rejected") return "#FCE8E8";
		return "transparent";
	}};
	padding: 4px 12px;
	border-radius: 12px;
`;

const ActionButtons = styled.div`
	display: flex;
	gap: 12px;
	justify-content: center;
`;

interface WithdrawalRequestModalProps {
	isOpen: boolean;
	onClose: () => void;
	request: WithdrawalRequest | null;
	onApprove: (requestId: string) => void;
	onDecline: (requestId: string) => void;
}

const WithdrawalRequestModal: React.FC<WithdrawalRequestModalProps> = ({
	isOpen,
	onClose,
	request,
	onApprove,
	onDecline,
}) => {
	if (!isOpen || !request) return null;

	const handleApprove = () => {
		onApprove(request._id);
	};

	const handleDecline = () => {
		onDecline(request._id);
	};

	return (
		<ModalOverlay onClick={onClose}>
			<ModalContent onClick={(e) => e.stopPropagation()}>
				<CloseButton onClick={onClose}>×</CloseButton>

				<Title>Withdrawal Request</Title>

				<UserSection>
					{/* <Avatar
						src={`https://via.placeholder.com/80?text=${request.user.first_name.charAt(
							0
						)}`}
						alt={`${request.user.first_name} ${request.user.last_name}`}
					/> */}
					<UserName>{`${request.user.first_name} ${request.user.last_name}`}</UserName>
					<UserType>
						{request.user_type.charAt(0).toUpperCase() +
							request.user_type.slice(1)}
					</UserType>
				</UserSection>

				<DetailsSection>
					<DetailRow>
						<DetailLabel>Request date:</DetailLabel>
						<DetailValue>
							{new Date(request.createdAt).toLocaleDateString()}
						</DetailValue>
					</DetailRow>

					<DetailRow>
						<DetailLabel>Status:</DetailLabel>
						<DetailValue status={request.request_status}>
							{request.request_status.charAt(0).toUpperCase() +
								request.request_status.slice(1)}
						</DetailValue>
					</DetailRow>

					<DetailRow>
						<DetailLabel>User Type:</DetailLabel>
						<DetailValue>
							{request.user_type.charAt(0).toUpperCase() +
								request.user_type.slice(1)}
						</DetailValue>
					</DetailRow>

					<DetailRow>
						<DetailLabel>Amount:</DetailLabel>
						<DetailValue>
							{request.user.currency_symbol}
							{request.amount.toLocaleString()}
						</DetailValue>
					</DetailRow>

					<DetailRow>
						<DetailLabel>Email:</DetailLabel>
						<DetailValue>{request.user.email}</DetailValue>
					</DetailRow>

					<DetailRow>
						<DetailLabel>Phone:</DetailLabel>
						<DetailValue>{request.user.phone}</DetailValue>
					</DetailRow>
				</DetailsSection>

				{request.request_status === "pending" && (
					<ActionButtons>
						<button
							onClick={handleDecline}
							className='border border-red-500 text-red-500 px-4 py-2 rounded-md w-full'
						>
							Decline
						</button>
						<button
							className='bg-primary text-white px-4 py-2 rounded-md w-full'
							onClick={handleApprove}
						>
							Approve
						</button>
					</ActionButtons>
				)}
			</ModalContent>
		</ModalOverlay>
	);
};

export default WithdrawalRequestModal;
